import { Link } from "react-router-dom";
import { Mail, Compass, Handshake, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Section";
import { SEO } from "@/components/SEO";
import { PAGE_SEO } from "@/lib/seoData";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { MarketPulseMap } from "@/components/MarketPulseMap";
import {
  KEY_FIGURES,
  STATS,
  LOOP,
  MOMENTUM,
  PRIMARY_AUDIENCE,
  SECONDARY_AUDIENCES,
  EXPANSION,
  TABLE_ROWS,
  INTERPRETATION_NOTES,
  REGIONS,
  FAQS,
  FAQ_JSON_LD,
  SOURCES,
} from "@/lib/marketPageData";

export default function Market() {
  return (
    <div data-testid="market-page">
      <SEO {...PAGE_SEO["/market"]} path="/market" jsonLd={FAQ_JSON_LD} />

      {/* 2.1 · HERO */}
      <section className="relative pt-40 pb-20 md:pt-48 md:pb-28 overflow-hidden border-b border-zinc-800" data-testid="market-hero">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[120vw] h-[55vh] radial-glow opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-accent block mb-6">
              Market Opportunity
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter leading-[1.02] max-w-4xl">
              The Gap Between Knowing and Doing Is a Market.
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-body text-lg md:text-xl text-zinc-400 mt-8 max-w-3xl leading-relaxed">
              Everyone knows sunscreen only works if you reapply it. Published research shows
              most people don't reapply enough, and forgetting is the most commonly cited
              reason. WearSpray exists for the hundreds of millions of people who already
              believe in protection, and just need it to be there when the moment comes.
            </p>
          </Reveal>
          <Reveal delay={0.16}>
            <MarketPulseMap className="mt-12 w-full max-w-2xl" />
          </Reveal>
        </div>
      </section>

      {/* 2.2 · THE PROBLEM, IN NUMBERS */}
      <section className="border-b border-zinc-800 bg-[#0d0d0d]" data-testid="market-stats">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent block mb-6">
              The Problem, In Numbers
            </span>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px mt-4 bg-zinc-800 border border-zinc-800">
            {STATS.map((s, i) => (
              <Reveal key={s.value} delay={i * 0.08}>
                <div className="bg-[#121212] p-8 h-full" data-testid={`market-stat-card-${i}`}>
                  <p className="font-heading text-5xl md:text-6xl font-light tracking-tighter text-accent">
                    {s.value}
                  </p>
                  <p className="font-body text-zinc-300 leading-relaxed mt-5">{s.body}</p>
                  <p className="font-mono text-[11px] text-zinc-500 leading-relaxed mt-6 border-t border-zinc-800 pt-4">
                    {s.source}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="font-heading text-xl md:text-2xl font-light tracking-tight leading-snug text-white mt-14 max-w-3xl border-l-2 border-accent pl-6 md:pl-8">
              The problem was never awareness. It's that the product is in the
              car, the bag, the locker, anywhere but on you, at the exact
              moment reapplication matters.{" "}
              <span className="text-accent">WearSpray moves protection onto the body itself.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* 2.3 · THE PLATFORM: TODAY vs IN DEVELOPMENT */}
      <section className="border-b border-zinc-800" data-testid="market-platform">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent block mb-6">
              The Platform
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-4xl">
              What's on wrists today. What's working today.{" "}
              <span className="text-accent">What's in development.</span>
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px mt-14 bg-zinc-800 border border-zinc-800">
            <Reveal>
              <div className="bg-[#121212] p-8 h-full" data-testid="market-platform-wrist">
                <span className="font-mono text-[10px] tracking-[0.18em] uppercase px-2.5 py-1 border border-[#00e5ff]/40 text-[#00e5ff]">
                  Available Today
                </span>
                <h3 className="font-heading text-xl font-medium text-white mt-5">On Wrists Today</h3>
                <p className="font-body text-zinc-400 text-sm leading-relaxed mt-3">
                  WearSpray is a patented wearable dispensing platform: a
                  refillable, reusable wrist-worn reservoir that puts your own
                  trusted sunscreen, lotion, repellent, or sanitizer one press
                  away. No bottle. No bag. No searching.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.06}>
              <div className="bg-[#121212] p-8 h-full" data-testid="market-platform-app">
                <span className="font-mono text-[10px] tracking-[0.18em] uppercase px-2.5 py-1 border border-[#00e5ff]/40 text-[#00e5ff]">
                  Working Prototype
                </span>
                <h3 className="font-heading text-xl font-medium text-white mt-5">Launching Soon</h3>
                <p className="font-body text-zinc-400 text-sm leading-relaxed mt-3">
                  A working prototype of the WearSpray companion app exists today
                  and is actively being developed ahead of public release, it is
                  not yet available in the App Store. The current build includes
                  a reapplication timer, UV and environmental intelligence, and
                  contextual reminders based on conditions, not arbitrary timers.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="bg-[#121212] p-8 h-full" data-testid="market-platform-sensor">
                <span className="font-mono text-[10px] tracking-[0.18em] uppercase px-2.5 py-1 border border-[#ff6fae]/40 text-[#ff6fae]">
                  In Development
                </span>
                <h3 className="font-heading text-xl font-medium text-white mt-5">The Sensor Module</h3>
                <p className="font-body text-zinc-400 text-sm leading-relaxed mt-3">
                  A compact wearable sensor module that reads perspiration,
                  skin temperature, activity, and exposure, feeding the app and
                  turning WearSpray from a smart dispenser into a personal
                  skincare intelligence.
                </p>
              </div>
            </Reveal>
          </div>

          {/* Sense → Understand → Prompt → Act → Learn */}
          <Reveal delay={0.15}>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mt-16 mb-6">
              The Loop
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-zinc-800 border border-zinc-800" data-testid="market-loop-diagram">
              {LOOP.map((s) => {
                const Icon = s.Icon;
                const isAct = s.t === "Act";
                return (
                  <div
                    key={s.t}
                    data-testid={`market-loop-${s.t.toLowerCase()}`}
                    className="relative p-6 md:p-7 h-full"
                    style={{ backgroundColor: isAct ? "#171012" : "#121212" }}
                  >
                    <div className="absolute inset-x-0 top-0 h-[3px]" style={{ backgroundColor: s.c }} />
                    <Icon size={22} strokeWidth={1.75} style={{ color: s.c }} />
                    <h4 className="font-heading text-lg font-medium mt-4" style={{ color: s.c }}>{s.t}</h4>
                    <p className="font-body text-zinc-400 text-xs leading-relaxed mt-2.5">{s.d}</p>
                    {isAct && (
                      <span className="font-mono text-[9px] tracking-[0.15em] uppercase text-[#ff2d78]/80 block mt-3">
                        The differentiator
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="font-mono text-[11px] text-zinc-600 leading-relaxed mt-5 max-w-3xl">
              The differentiating step is Act: reminder apps stop at the
              notification. WearSpray puts the product at the point of need.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 2.4 · WHO ADOPTS FIRST */}
      <section className="border-b border-zinc-800 bg-[#0d0d0d]" data-testid="market-who">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent block mb-6">
              Who Adopts First
            </span>
            <p className="font-heading text-2xl md:text-3xl font-light tracking-tight leading-relaxed max-w-4xl text-zinc-100" data-testid="market-primary-audience">
              Initial commercial focus: <span className="text-accent">{PRIMARY_AUDIENCE.t}.</span>{" "}
              {PRIMARY_AUDIENCE.d}
            </p>
          </Reveal>
          <Reveal delay={0.06}>
            <p className="font-mono text-[11px] text-zinc-500 leading-relaxed mt-8 max-w-3xl">
              "Initial commercial focus," not "designed for": WearSpray is a
              refillable dispensing platform compatible with a broad range of
              over-the-counter liquids, lotions and gels, so the product isn't
              boxed permanently into sunscreen, one gender, beach use, or a
              single age group.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mt-16 mb-6">
              Secondary Audiences
            </p>
            <div className="overflow-x-auto border border-zinc-800" data-testid="market-secondary-audiences">
              <table className="w-full min-w-[640px] text-left border-collapse">
                <thead>
                  <tr className="bg-[#121212] border-b border-zinc-800">
                    <th className="font-mono text-xs tracking-[0.15em] uppercase text-zinc-500 p-5">Audience</th>
                    <th className="font-mono text-xs tracking-[0.15em] uppercase text-zinc-500 p-5 border-l border-zinc-800">Need state</th>
                    <th className="font-mono text-xs tracking-[0.15em] uppercase text-zinc-500 p-5 border-l border-zinc-800">Likely channels</th>
                  </tr>
                </thead>
                <tbody>
                  {SECONDARY_AUDIENCES.map((a, i) => (
                    <tr key={a.t} className={i % 2 === 1 ? "bg-[#0d0d0d]" : ""} data-testid={`market-secondary-audience-${i}`}>
                      <td className="font-heading text-sm text-white p-5 border-t border-zinc-800">{a.t}</td>
                      <td className="font-body text-sm text-zinc-400 p-5 border-t border-l border-zinc-800">{a.need}</td>
                      <td className="font-body text-sm text-zinc-400 p-5 border-t border-l border-zinc-800">{a.channels}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 2.5 · MARKET AT A GLANCE */}
      <section className="border-b border-zinc-800" data-testid="market-glance">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent block mb-6">
              Market at a Glance
            </span>
          </Reveal>
          <Reveal delay={0.06}>
            <div className="overflow-x-auto border border-zinc-800" data-testid="market-glance-table">
              <table className="w-full min-w-[720px] text-left border-collapse">
                <thead>
                  <tr className="bg-[#121212] border-b border-zinc-800">
                    <th className="font-mono text-xs tracking-[0.15em] uppercase text-zinc-500 p-5">Market measure</th>
                    <th className="font-mono text-xs tracking-[0.15em] uppercase text-zinc-500 p-5 border-l border-zinc-800">United States</th>
                    <th className="font-mono text-xs tracking-[0.15em] uppercase text-zinc-500 p-5 border-l border-zinc-800">Europe (EU-27 + UK)</th>
                    <th className="font-mono text-xs tracking-[0.15em] uppercase text-zinc-500 p-5 border-l border-zinc-800">Caribbean</th>
                  </tr>
                </thead>
                <tbody>
                  {TABLE_ROWS.map((r, i) => (
                    <tr key={r.label} className={i % 2 === 1 ? "bg-[#0d0d0d]" : ""} data-testid={`market-glance-row-${i}`}>
                      <td className="font-body text-sm text-zinc-200 p-5 border-t border-zinc-800">{r.label}</td>
                      <td className="font-heading text-base text-white p-5 border-t border-l border-zinc-800">{r.us}</td>
                      <td className="font-heading text-base text-white p-5 border-t border-l border-zinc-800">{r.eu}</td>
                      <td className="font-heading text-base text-white p-5 border-t border-l border-zinc-800">{r.carib}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-10 border-l-2 border-accent pl-6 md:pl-8 max-w-4xl">
              <p className="font-heading text-xl md:text-2xl font-light tracking-tight leading-snug text-white">
                Combined: approximately <span className="text-accent">{KEY_FIGURES.combinedPop2544}</span> people
                ages 25–44 across the three regions, and an estimated{" "}
                <span className="text-accent">{KEY_FIGURES.coreAudience}</span> core, need-based WearSpray consumers.
              </p>
              <p className="font-body text-zinc-400 text-sm leading-relaxed mt-4">
                Caribbean visitor arrivals are deliberately not added to the
                total, many tourists originate in the U.S. or Europe and would
                be double-counted. They represent sales occasions, not
                additional people.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.14}>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mt-16 mb-6">
              How to Read These Numbers
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800" data-testid="market-interpretation-notes">
              {INTERPRETATION_NOTES.map((n) => (
                <div key={n.t} className="bg-[#121212] p-7 h-full">
                  <h4 className="font-heading text-base font-medium text-white">{n.t}</h4>
                  <p className="font-body text-zinc-400 text-sm leading-relaxed mt-3">{n.d}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* 2.6 · REGION SNAPSHOTS */}
      <section className="border-b border-zinc-800 bg-[#0d0d0d]" data-testid="market-regions">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent block mb-6">
              Region Snapshots
            </span>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            {REGIONS.map((r, i) => {
              const Icon = r.Icon;
              return (
                <Reveal key={r.t} delay={i * 0.08}>
                  <div className="border border-zinc-800 bg-[#121212] p-8 h-full" data-testid={`market-region-${i}`}>
                    <Icon size={22} strokeWidth={1.5} className="text-accent" />
                    <h3 className="font-heading text-xl font-medium text-white mt-5">{r.t}</h3>
                    <p className="font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-500 mt-1">{r.tag}</p>
                    <p className="font-body text-zinc-400 text-sm leading-relaxed mt-5">{r.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 2.7 · CATEGORY MOMENTUM ("WHY NOW") */}
      <section className="border-b border-zinc-800" data-testid="market-momentum">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent block mb-6">
              Why Now
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-4xl">
              Sun care is moving beyond a{" "}
              <span className="text-accent">seasonal beach purchase.</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px mt-14 bg-zinc-800 border border-zinc-800" data-testid="market-momentum-grid">
            {MOMENTUM.map((m, i) => (
              <Reveal key={m.v} delay={i * 0.06}>
                <div className="bg-[#121212] p-7 h-full" data-testid={`market-momentum-card-${i}`}>
                  <p className="font-heading text-3xl md:text-4xl font-light tracking-tighter text-accent">{m.v}</p>
                  <p className="font-body text-zinc-300 text-sm leading-relaxed mt-4">{m.t}</p>
                  <p className="font-mono text-[10px] text-zinc-500 mt-4 border-t border-zinc-800 pt-3">{m.source}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 2.9 · EXPANSION (UNDER EVALUATION, NOT CORE TAM) */}
      <section className="border-b border-zinc-800 bg-[#0d0d0d]" data-testid="market-expansion">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent block mb-6">
              Expansion Opportunities Under Evaluation
            </span>
            <p className="font-heading text-xl md:text-2xl font-light tracking-tight leading-snug max-w-4xl text-zinc-100">
              Beyond the United States, Europe and the Caribbean, WearSpray is
              evaluating South America and South Africa. These regions are{" "}
              <span className="text-accent">not included</span> in the core
              audience figures above, they represent future localization and
              partnership opportunities, not current serviceable demand.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
            {EXPANSION.map((e, i) => (
              <Reveal key={e.t} delay={i * 0.08}>
                <div className="border border-zinc-800 bg-[#121212] p-8 h-full" data-testid={`market-expansion-${i}`}>
                  <Compass size={22} strokeWidth={1.5} className="text-accent" />
                  <h3 className="font-heading text-xl font-medium text-white mt-5">{e.t}</h3>
                  <p className="font-body text-zinc-400 text-sm leading-relaxed mt-5">{e.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 2.7 · FAQ */}
      <section className="border-b border-zinc-800" data-testid="market-faq">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent block mb-6">
              Frequently Asked Questions
            </span>
          </Reveal>
          <Reveal delay={0.06}>
            <Accordion type="single" collapsible className="w-full" data-testid="market-faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`} className="border-zinc-800" data-testid={`market-faq-item-${i}`}>
                  <AccordionTrigger
                    className="font-heading text-base md:text-lg font-medium text-white hover:no-underline py-5"
                    data-testid={`market-faq-trigger-${i}`}
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="font-body text-zinc-400 text-sm md:text-base leading-relaxed" data-testid={`market-faq-content-${i}`}>
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <Reveal delay={0.1}>
            <a
              href="mailto:greg@wearspray.com"
              data-testid="market-faq-contact"
              className="group inline-flex items-center gap-2 font-body text-sm text-accent mt-10 hover:opacity-80 transition-opacity"
            >
              <Mail size={15} /> greg@wearspray.com
            </a>
          </Reveal>
        </div>
      </section>

      {/* PARTNER CALL TO ACTION */}
      <section className="border-b border-zinc-800 bg-[#0d0d0d]" data-testid="market-partner-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-accent flex items-center gap-2 mb-6">
              <Handshake size={14} /> Partner With WearSpray
            </span>
            <p className="font-heading text-xl md:text-2xl font-light tracking-tight leading-snug max-w-4xl text-zinc-100">
              WearSpray is preparing for market entry across consumer,
              retail, hospitality and outdoor channels. We welcome
              conversations with distributors, resorts, retailers, clinicians
              and brand partners who see the opportunity to make personal
              care more accessible, wearable and responsive.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
            {[
              { t: "Clinical Collaboration", d: "Run a structured pilot testing whether easier access changes how consistently patients follow a recommended regimen.", topic: "Clinical Collaboration" },
              { t: "Brand Licensing & Product Development", d: "License the dispensing platform, or co-develop a branded dispensing format around your formulation and packaging.", topic: "Brand Licensing & Product Development" },
              { t: "Strategic Investment", d: "Back a manufactured, patented product foundation as WearSpray scales across consumer, clinical and global-health channels.", topic: "Strategic Investment" },
            ].map((p) => (
              <Reveal key={p.t} delay={0.06}>
                <Link
                  to={`/contact?topic=${encodeURIComponent(p.topic)}`}
                  data-testid={`market-partner-path-${p.topic.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group flex flex-col h-full border border-zinc-800 bg-[#121212] p-7 hover:border-accent/40 transition-colors"
                >
                  <h3 className="font-heading text-lg font-medium text-white">{p.t}</h3>
                  <p className="font-body text-zinc-400 text-sm leading-relaxed mt-3">{p.d}</p>
                  <span className="inline-flex items-center gap-2 font-body text-sm text-accent mt-auto pt-5 group-hover:gap-3 transition-all">
                    Start a conversation <ArrowRight size={14} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.08}>
            <a
              href="mailto:greg@wearspray.com"
              data-testid="market-partner-cta-contact"
              className="group inline-flex items-center gap-2 font-body text-sm font-medium text-zinc-400 hover:text-accent transition-colors mt-10"
            >
              <Mail size={15} /> Or email greg@wearspray.com directly
            </a>
          </Reveal>
        </div>
      </section>

      {/* 2.8 · SOURCES */}
      <section className="bg-[#0a0a0a]" data-testid="market-sources">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
          <p className="font-mono text-[11px] text-zinc-500 leading-relaxed max-w-4xl mb-8" data-testid="market-methodology-note">
            <span className="text-zinc-400 tracking-[0.15em] uppercase block mb-3">Methodology</span>
            Population figures use the latest available official or United
            Nations data at the time of research. Core and launch audience
            ranges are WearSpray planning estimates derived by applying
            behavioral and commercial filters to the age 25–44 population;
            they are directional ranges, not third-party forecasts. Tourism
            visits are shown separately and are never added to resident
            population totals. The reservoir and companion app status
            described above reflect the current product stage as supplied
            by WearSpray.
          </p>
          <p className="font-mono text-[11px] text-zinc-500 leading-relaxed max-w-4xl">
            <span className="text-zinc-400 tracking-[0.15em] uppercase block mb-3">Sources</span>
            {SOURCES}
          </p>
          <Link
            to="/contact"
            data-testid="market-sources-contact-link"
            className="font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-500 hover:text-accent transition-colors mt-6 inline-block"
          >
            Questions about this data? Get in touch →
          </Link>
        </div>
      </section>
    </div>
  );
}
